/**
 * FAQStats Component
 *
 * Displays FAQ statistics cards for moderators
 * Extracted from FAQs.tsx as part of Phase 3 decomposition
 *
 * Responsibilities:
 * - Display 4 statistic cards (pending, approved, rejected, total)
 * - Handle loading state with skeletons
 * - Color-coded icons for each stat type
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface FAQStatsProps {
  /** FAQ statistics data */
  stats: {
    pending_review: number;
    approved_faqs: number;
    rejected_faqs: number;
    total_faqs: number;
  } | undefined;
  /** Whether stats are currently loading */
  isLoading: boolean;
}

/**
 * Memoized Stat Card Component
 */
const StatCard = React.memo<{
  icon: React.ReactNode;
  value: number;
  label: string;
  color: 'warning' | 'success' | 'error' | 'primary';
  isLoading: boolean;
}>(({ icon, value, label, color, isLoading }) => {
  return (
    <Card>
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="h4" color={`${color}.main`}>
            {isLoading ? <Skeleton width={60} /> : value}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
});

StatCard.displayName = 'StatCard';

/**
 * FAQStats - Display FAQ statistics for moderators
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 * Each stat card is also memoized individually
 *
 * @example
 * ```tsx
 * <FAQStats
 *   stats={{
 *     pending_review: 5,
 *     approved_faqs: 42,
 *     rejected_faqs: 3,
 *     total_faqs: 50
 *   }}
 *   isLoading={false}
 * />
 * ```
 */
export const FAQStats = React.memo<FAQStatsProps>(({ stats, isLoading }) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* Pending Review */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<PendingIcon color="warning" />}
          value={stats?.pending_review || 0}
          label={t('faq.moderation.stats.pending_review')}
          color="warning"
          isLoading={isLoading}
        />
      </Grid>

      {/* Approved */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<ApprovedIcon color="success" />}
          value={stats?.approved_faqs || 0}
          label={t('faq.moderation.stats.approved')}
          color="success"
          isLoading={isLoading}
        />
      </Grid>

      {/* Rejected */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<RejectedIcon color="error" />}
          value={stats?.rejected_faqs || 0}
          label={t('faq.moderation.stats.rejected')}
          color="error"
          isLoading={isLoading}
        />
      </Grid>

      {/* Total */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<CategoryIcon color="primary" />}
          value={stats?.total_faqs || 0}
          label={t('faq.stats.totalFaqs')}
          color="primary"
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
});

FAQStats.displayName = 'FAQStats';
